class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    // One letter matches half the catalogue; wait for a second character.
    if (this.searchTerm.length < 2) {
      this.close();
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);

    switch (event.code) {
      case 'ArrowUp':
        event.preventDefault();
        this.switchOption('up');
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
      case 'Escape':
        this.close();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
    // Prevent native <input type="search"> from clearing the value on Escape
    if (event.code === 'Escape') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const limit = this.getResultsLimit();
    const queryKey = `${searchTerm.replace(' ', '-').toLowerCase()}-${limit}`;
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(
        searchTerm
      )}&section_id=predictive-search&resources[limit]=${limit}&resources[limit_scope]=each&resources[type]=product,query,collection`,
      {
        signal: this.abortController.signal,
      }
    )
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
    this.renderResultsCount(this.searchTerm);
  }

  // "Show all N products" — the predictive API has no total, so ask the search page (search.count template).
  renderResultsCount(searchTerm) {
    if (!this.querySelector('#predictive-search-results-products-list')) return;
    const apply = (label) => {
      const target = this.querySelector('[data-predictive-search-search-for-text]');
      if (label && target && this.searchTerm === searchTerm) target.textContent = label;
    };
    PredictiveSearch.countCache = PredictiveSearch.countCache || {};
    if (PredictiveSearch.countCache[searchTerm]) {
      apply(PredictiveSearch.countCache[searchTerm]);
      return;
    }
    fetch(
      `${routes.search_url}?q=${encodeURIComponent(searchTerm)}&options%5Bprefix%5D=last&type=product&view=count`,
      { signal: this.abortController.signal }
    )
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (!data?.label) return;
        PredictiveSearch.countCache[searchTerm] = data.label;
        apply(data.label);
      })
      .catch(() => {});
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  // Phone search sheet (header search modal under 750px): results fill the space above the keyboard.
  isPhoneSheet() {
    return !!this.closest('.search-modal') && window.matchMedia('(max-width: 749px)').matches;
  }

  getResultsMaxHeight() {
    // visualViewport shrinks when the on-screen keyboard opens; innerHeight does not (Android Chrome).
    const viewport = window.visualViewport;
    const visibleBottom = viewport ? viewport.offsetTop + viewport.height : window.innerHeight;
    const top = this.isPhoneSheet()
      ? this.predictiveSearchResults.getBoundingClientRect().top
      : document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    this.resultsMaxHeight = Math.max(160, visibleBottom - top - (this.isPhoneSheet() ? 0 : 16));
    return this.resultsMaxHeight;
  }

  onViewportChange() {
    if (!this.isOpen) return;
    this.predictiveSearchResults.style.maxHeight = `${this.getResultsMaxHeight()}px`;
  }

  getResultsLimit() {
    if (this.isPhoneSheet()) return 6; // the phone list scrolls, so always ask for the full six
    const ITEM_HEIGHT = 90;
    const SHOW_ALL_HEIGHT = 59;
    const available = this.getResultsMaxHeight();
    const limit = Math.floor((available - SHOW_ALL_HEIGHT) / ITEM_HEIGHT);
    return Math.min(6, Math.max(4, limit));
  }

  open() {
    this.setAttribute('open', true);
    this.predictiveSearchResults.style.maxHeight = `${this.getResultsMaxHeight()}px`;
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
    if (!this.viewportListener && window.visualViewport) {
      this.viewportListener = this.onViewportChange.bind(this);
      window.visualViewport.addEventListener('resize', this.viewportListener);
    }
    // On the phone sheet the list scrolls inside itself, so nothing is cut.
    if (!this.isPhoneSheet()) this.trimOverflowingResults();
  }

  trimOverflowingResults() {
    const container = this.predictiveSearchResults;
    if (!container) return;
    const productItems = [
      ...container.querySelectorAll('#predictive-search-results-products-list .predictive-search__list-item'),
    ];
    // Reset any previously hidden items before trimming
    productItems.forEach((el) => el.style.removeProperty('display'));
    let i = productItems.length - 1;
    while (container.scrollHeight > container.clientHeight && i >= 4) {
      productItems[i].style.display = 'none';
      i--;
    }
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
    if (this.viewportListener) {
      window.visualViewport.removeEventListener('resize', this.viewportListener);
      this.viewportListener = null;
    }
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
